import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { useVariable } from '@softwareimaging/backstage';
import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { create } from 'zustand';
import { Variables } from '../backstage/config';
import { AppInsightsContext, AppInsightsStore } from './useAppInsights';

export const AppInsightsProvider: FC<PropsWithChildren> = ({ children }) => {
  const connectionString = useVariable<Variables>('appInsightsConnectionString');
  if (!connectionString) throw new Error('AppInsights connection string is not set');

  const [appInsights] = useState<ApplicationInsights>(() => {
    const reactPlugin = new ReactPlugin();
    const insights = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: true,
        extensions: [reactPlugin]
      }
    });
    return insights;
  });

  useEffect(() => {
    if (import.meta.env.DEV) {
      //console.warn('AppInsights is not enabled in development');
      return;
    }

    if (appInsights.appInsights.isInitialized()) {
      return;
    }

    appInsights.loadAppInsights();
  }, [appInsights]);

  const store = useMemo(
    () =>
      create<AppInsightsStore>(() => ({
        getSessionId: () => {
          if (!appInsights.appInsights.isInitialized()) {
            return 'No session in development';
          }
          return appInsights.context?.getSessionId();
        },
        trackException: (exception: Error) => {
          if (!appInsights) {
            console.error(exception);
            return;
          }
          appInsights.trackException({ exception });
        },
        addAuthenticatedUserContext: (userId: string, partnerId?: string) => {
          if (!appInsights.appInsights.isInitialized()) {
            //console.warn(`AppInsights is not enabled in development: ${userId}`);
            return;
          }
          appInsights.setAuthenticatedUserContext(userId, partnerId, true);
        },
        trackEvent: (name: string, properties?: { [key: string]: string }) => {
          if (!appInsights.appInsights.isInitialized()) {
            //console.warn(`AppInsights is not enabled in development: ${name}`);
            return;
          }
          appInsights.trackEvent({ name }, properties);
        }
      })),
    [appInsights]
  );

  return <AppInsightsContext.Provider value={store}>{children}</AppInsightsContext.Provider>;
};

